import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/scripts/AuthContext";
import { useOrgSocialActor } from "@/scripts/OrgSocialActorContext";
import { useSocialPaths } from "@/lib/socialPaths";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError, toastSuccess } from "@/lib/toast";

export default function OrgSocialProfileEditPage() {
  const { accessToken } = useAuth();
  const { orgId, canPublish, orgProfile, handle, reloadShell } =
    useOrgSocialActor();
  const paths = useSocialPaths();

  const [profileDraft, setProfileDraft] = useState({
    display_name: "",
    bio: "",
    location: "",
    website_url: "",
  });
  const [communities, setCommunities] = useState([]);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  useEffect(() => {
    setProfileDraft({
      display_name: orgProfile?.display_name || "",
      bio: orgProfile?.bio || "",
      location: orgProfile?.location || "",
      website_url: orgProfile?.website_url || "",
    });
    setCommunities(orgProfile?.communities || []);
  }, [orgProfile]);

  const saveProfile = async () => {
    if (!canPublish) return;
    try {
      const res = await axios.patch(
        `${API_BASE_URL}/org/${orgId}/social/profile`,
        profileDraft,
        { headers }
      );
      toastSuccess("Profile updated");
      if (res.data) {
        setProfileDraft({
          display_name: res.data.display_name || "",
          bio: res.data.bio || "",
          location: res.data.location || "",
          website_url: res.data.website_url || "",
        });
        setCommunities(res.data.communities || communities);
      }
      if (typeof reloadShell === "function") await reloadShell();
    } catch (err) {
      toastError(err, "Could not save profile");
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 space-y-6">
      {handle ? (
        <Link
          to={paths.org(handle)}
          className="inline-flex text-sm text-muted-foreground hover:text-foreground hover:underline"
        >
          ← Back to profile
        </Link>
      ) : null}
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Edit Circa Social profile</h2>
        {handle ? (
          <Button type="button" variant="outline" size="sm" asChild>
            <Link to={paths.org(handle)}>View public profile</Link>
          </Button>
        ) : null}
      </div>

      <div className="space-y-3">
        <div>
          <Label>Display name</Label>
          <Input
            value={profileDraft.display_name}
            onChange={(e) =>
              setProfileDraft((d) => ({
                ...d,
                display_name: e.target.value,
              }))
            }
            disabled={!canPublish}
          />
        </div>
        <div>
          <Label>Bio</Label>
          <textarea
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={profileDraft.bio}
            onChange={(e) =>
              setProfileDraft((d) => ({ ...d, bio: e.target.value }))
            }
            disabled={!canPublish}
          />
        </div>
        <div>
          <Label>Location</Label>
          <Input
            value={profileDraft.location}
            onChange={(e) =>
              setProfileDraft((d) => ({
                ...d,
                location: e.target.value,
              }))
            }
            disabled={!canPublish}
          />
        </div>
        <div>
          <Label>Website</Label>
          <Input
            value={profileDraft.website_url}
            onChange={(e) =>
              setProfileDraft((d) => ({
                ...d,
                website_url: e.target.value,
              }))
            }
            disabled={!canPublish}
          />
        </div>
        {canPublish ? (
          <Button type="button" onClick={saveProfile}>
            Save profile
          </Button>
        ) : null}
      </div>

      {(communities || []).length > 0 ? (
        <div className="space-y-3 pt-4 border-t border-border">
          <h3 className="text-sm font-medium">Communities</h3>
          {communities.map((c) => (
            <Link
              key={c.community_id}
              to={paths.community(c.community_id)}
              className="block rounded-lg border p-4 hover:bg-muted/40 transition-colors"
            >
              <p className="font-medium">{c.display_name}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {c.is_primary_for_org ? "Primary · " : ""}
                {c.posting_policy}
              </p>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}
